// app/components/Connectors/NINXConnector.ts:
import { BrokerConnector, Order, Position, OptionsChainResponse } from './types'
// Fix duplicate import
import {
  Spot,
  Side,
  OrderType as NINXOrderType
} from '@binance/connector-typescript'
import {
  getSecureItem,
  setSecureItem,
  removeSecureItem
} from './utils/secureStorage'
import { ninxAuthService, type NinxLoginRequest } from '../../lib/ninx-auth'
import { hasValidNinxSession, getNinxKeys, clearNinxSession } from '../../lib/auth-cookies'

type chartOrders = {
  time: string
  symbol: string
  side: 'BUY' | 'SELL'
  limitPrice: number
  size: number
  stopLoss?: number
  takeProfit?: number
  orderId?: number
}

// Update types to match the actual API responses
type NINXBalance = {
  asset: string
  free: string
  locked: string
}

// Update the account info type to match the actual response
interface NINXAccountInfo {
  makerCommission: number
  takerCommission: number
  buyerCommission: number
  sellerCommission: number
  canTrade: boolean
  canWithdraw: boolean
  canDeposit: boolean
  updateTime: number
  accountType: string
  balances: NINXBalance[]
  permissions: string[]
}

type NINXTicker = {
  symbol: string
  price: string
}

/**
 * NINX trading connector implementation
 * Provides integration with NINX cryptocurrency exchange API
 * 
 * This connector enables cryptocurrency trading on NINX, including spot and futures markets.
 * It supports various order types, market data retrieval, and account management features.
 * 
 * @example
 * // Initialize the connector with API credentials
 * const ninxConnector = new NINXConnector('your-api-key', 'your-api-secret');
 * 
 * // Connect to NINX
 * await ninxConnector.connect();
 * 
 * // Place a market order
 * const order = await ninxConnector.placeOrder({
 *   symbol: 'BTCUSDT',
 *   quantity: 0.001,
 *   type: 'MARKET',
 *   side: 'BUY'
 * });
 */
  export class NINXConnector implements NINXConnector {
  private apiKey: string
  private apiSecret: string
  private isConnected: boolean = false
  private client: Spot | null = null

  /**
   * Create a new NINX connector instance
   * 
   * @param {string} apiKey The API key obtained from NINX (optional, will be retrieved from auth service)
   * @param {string} apiSecret The API secret obtained from NINX (optional, will be retrieved from auth service)
   * 
   * @example
   * const connector = new NINXConnector();
   */
  constructor(apiKey?: string, apiSecret?: string) {
    // Get credentials from auth service or use provided ones
    if (typeof window !== 'undefined') {
      const { apiKey: storedApiKey, apiSecret: storedApiSecret } = getNinxKeys();
      this.apiKey = apiKey || storedApiKey || ''
      this.apiSecret = apiSecret || storedApiSecret || ''

      // Check if we have valid NINX session
      if (hasValidNinxSession() && this.apiKey && this.apiSecret) {
        this.isConnected = true
        this.client = new Spot(this.apiKey, this.apiSecret, {
          baseURL: 'https://api.binance.com'
        })
        console.log('NINXConnector initialized from stored session')
      }
    } else {
      this.apiKey = apiKey || ''
      this.apiSecret = apiSecret || ''
    }
  }

  /**
   * Check if the connector is currently connected to NINX
   * 
   * @returns {boolean} True if connected with valid credentials, false otherwise
   * 
   * @example
   * if (ninxConnector.isConnectedToBroker()) {
   *   // Proceed with trading operations
   * } else {
   *   // Show login UI
   * }
   */
  isConnectedToBroker(): boolean {
    // Check if we have valid session and credentials, or are actually connected
    // This allows the connector to be used even before connect() is called
    const hasValidSession = hasValidNinxSession()
    const hasCredentials = !!(this.apiKey && this.apiSecret)
    const isActuallyConnected = this.isConnected && this.client !== null

    return isActuallyConnected || (hasValidSession && hasCredentials)
  }

  /**
   * Make an authenticated API request with automatic token refresh on 401 errors
   */
  private async makeAuthenticatedRequest(endpoint: string, body: any, retryCount = 0): Promise<any> {
    try {
      if (!this.isConnectedToBroker()) {
        throw new Error('Not connected to NINX, please sign-in to connect to NINX')
      }

      // Make the API request
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      })

      // If we get a 401 error, try to refresh the session and retry once
      if (response.status === 401 && retryCount === 0) {
        console.log('🔄 Received 401 error, attempting automatic token refresh...')

        try {
          // Try to refresh the session
          const refreshResult = await ninxAuthService.refreshSession()

          if (refreshResult && refreshResult.status) {
            console.log('✅ Token refreshed successfully, retrying request...')

            // Update our credentials if they were refreshed
            if (refreshResult.ninx_keys) {
              this.apiKey = refreshResult.ninx_keys.api_key
              this.apiSecret = refreshResult.ninx_keys.api_secret
              body.apiKey = this.apiKey
              body.apiSecret = this.apiSecret
            } else {
              // If refresh response doesn't include keys, try to restore them from storage
              console.log('🔄 Refresh response missing NINX keys, attempting to restore from storage...')
              const { apiKey: storedApiKey, apiSecret: storedApiSecret } = getNinxKeys()
              if (storedApiKey && storedApiSecret) {
                this.apiKey = storedApiKey
                this.apiSecret = storedApiSecret
                body.apiKey = this.apiKey
                body.apiSecret = this.apiSecret
                console.log('✅ NINX keys restored from storage')
              } else {
                // If still no keys, try auto-authentication to restore them
                console.log('🔄 No stored NINX keys found, attempting auto-authentication...')
                const authResult = await ninxAuthService.autoAuthenticate()
                if (authResult) {
                  const { apiKey: newApiKey, apiSecret: newApiSecret } = getNinxKeys()
                  if (newApiKey && newApiSecret) {
                    this.apiKey = newApiKey
                    this.apiSecret = newApiSecret
                    body.apiKey = this.apiKey
                    body.apiSecret = this.apiSecret
                    console.log('✅ NINX keys restored via auto-authentication')
                  } else {
                    throw new Error('Auto-authentication succeeded but NINX keys not found')
                  }
                } else {
                  throw new Error('Auto-authentication failed')
                }
              }
            }

            // Retry the request with refreshed/restored credentials
            return this.makeAuthenticatedRequest(endpoint, body, retryCount + 1)
          } else {
            console.log('❌ Token refresh failed, user needs to re-authenticate')
            throw new Error('Session expired, please sign-in again')
          }
        } catch (refreshError) {
          console.error('❌ Token refresh failed:', refreshError)
          throw new Error('Session expired, please sign-in again')
        }
      }

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`)
      }

      const data = await response.json()
      return data
    } catch (error: any) {
      console.error(`Error making authenticated request to ${endpoint}:`, error)
      throw error
    }
  }

  /**
   * Place a Good Till Triggered (GTT) order on NINX
   * Note: This is a mock implementation as NINX doesn't directly support GTT orders
   * 
   * @param {Partial<Order>} order The GTT order details
   * @returns {Promise<Order>} Promise resolving to the placed GTT order
   * @throws {Error} If not connected or order parameters are invalid
   * 
   * @example
   * const gttOrder = await ninxConnector.placeGTT({
   *   symbol: 'BTCUSDT',
   *   quantity: 0.001,
   *   price: 25000,
   *   side: 'BUY',
   *   triggerPrice: 26000,
   *   triggerType: 'SINGLE'
   * });
   */
  async placeGTT(order: Partial<Order>): Promise<Order> {
    if (!this.isConnected) {
      throw new Error('Not connected to NINX')
    }

    try {
      const { symbol, quantity, price, side, triggerPrice, triggerType } = order

      if (!symbol || !quantity || !side || !triggerPrice) {
        throw new Error('Symbol, quantity, side, and trigger price are required for GTT orders')
      }

      // This is a mock implementation since NINX doesn't directly support GTT orders
      // In a real implementation, you might use NINX's OCO orders or other alternatives
      console.log('Mock GTT order placed with NINX:', { symbol, quantity, price, side, triggerPrice, triggerType })

      // Return a mock order response
      return {
        orderId: `GTT-MOCK-${Date.now()}`,
        symbol: symbol,
        quantity: quantity,
        price: price || triggerPrice,
        type: 'GTT',
        side: side,
        status: 'OPEN',
        triggerPrice: triggerPrice,
        triggerType: triggerType || 'SINGLE'
      }
    } catch (error) {
      console.error('Failed to place GTT order:', error)
      throw error
    }
  }

  /**
   * Get WebSocket URL for NINX real-time data
   * 
   * @returns {string} The WebSocket URL for connecting to NINX streams
   * 
   * @example
   * const wsUrl = NINXConnector.getWebSocketUrl();
   * const ws = new WebSocket(`${wsUrl}/btcusdt@trade`);
   */
  getWebSocketUrl(): string {
    return 'wss://stream.binance.com:9443/ws'
  }

  /**
   * Connect to the NINX exchange
   * Uses the NINX authentication service to establish connection
   * 
   * @returns {Promise<boolean>} Promise resolving to true if connection is successful
   * @throws {Error} If authentication fails or connection cannot be established
   * 
   * @example
   * try {
   *   const connected = await ninxConnector.connect();
   *   if (connected) {
   *     console.log('Connected to NINX');
   *   }
   * } catch (error) {
   *   console.error('Connection failed:', error);
   * }
   */
  async connect(): Promise<boolean> {
    try {
      console.log('NINXConnector: Attempting connection...')

      // First priority: Check if we already have valid credentials and session
      if (hasValidNinxSession() && this.apiKey && this.apiSecret) {
        console.log('NINXConnector: Using existing valid session and credentials')

        // Initialize the NINX Spot client if not already done
        if (!this.client) {
          this.client = new Spot(this.apiKey, this.apiSecret, {
            baseURL: 'https://api.binance.com'
          })
        }

        this.isConnected = true
        console.log('NINXConnector: Connection established with existing credentials')
        return true
      }

      // Second priority: Try to auto-authenticate with the NINX service
      const authResult = await ninxAuthService.autoAuthenticate()

      if (!authResult) {
        console.log('NINXConnector: Auto-authentication failed and no existing valid session')
        return false
      }

      // Get the API keys from the auth service
      const { apiKey, apiSecret } = getNinxKeys()
      
      if (!apiKey || !apiSecret) {
        console.log('NINXConnector: No API keys available after authentication')
        return false
      }

      // Store the keys locally
      this.apiKey = apiKey
      this.apiSecret = apiSecret

      // Initialize the NINX Spot client
      this.client = new Spot(this.apiKey, this.apiSecret, {
        baseURL: 'https://api.binance.com'
      })

      // Test the connection by fetching account info
      const response = await fetch('/api/connectors/ninx/account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          apiSecret: this.apiSecret
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to verify NINX connection')
      }

      const accountInfo = await response.json()

      if (!accountInfo) {
        throw new Error('Failed to fetch account information')
      }

      this.isConnected = true
      console.log('NINXConnector: Connection established successfully via authentication')
      return true

    } catch (error) {
      console.error('NINXConnector: Connection failed:', error)
      this.isConnected = false
      this.client = null
      return false
    }
  }

  /**
   * Disconnect from the NINX exchange
   * Uses the NINX authentication service to properly logout and clear session data
   * 
   * @returns {Promise<boolean>} Promise resolving to true if disconnection is successful
   * 
   * @example
   * await ninxConnector.disconnect();
   * console.log('Disconnected from NINX');
   */
  async disconnect(): Promise<boolean> {
    try {
      console.log('NINXConnector: Disconnecting...')

      // Use the auth service to logout and clear session data
      const logoutResult = await ninxAuthService.logout()

      // Reset local state regardless of logout result
      this.client = null
      this.isConnected = false
      this.apiKey = ''
      this.apiSecret = ''

      console.log('NINXConnector: Disconnected successfully')
      return logoutResult
    } catch (error) {
      console.error('NINXConnector: Disconnection failed:', error)
      
      // Still reset local state even if logout fails
      this.client = null
      this.isConnected = false
      this.apiKey = ''
      this.apiSecret = ''
      
      return false
    }
  }

  /**
   * Place a trading order on NINX
   * Supports various order types including MARKET, LIMIT, STOP_LOSS, and STOP_LOSS_LIMIT
   * 
   * @param {Partial<Order>} order The order details including symbol, quantity, price, etc.
   * @returns {Promise<Order>} Promise resolving to the placed order with order ID and status
   * @throws {Error} If not connected or order parameters are invalid
   * 
   * @example
   * // Place a market buy order
   * const marketOrder = await ninxConnector.placeOrder({
   *   symbol: 'BTCUSDT',
   *   quantity: 0.001,
   *   type: 'MARKET',
   *   side: 'BUY'
   * });
   * 
   * @example
   * // Place a limit sell order
   * const limitOrder = await ninxConnector.placeOrder({
   *   symbol: 'ETHUSDT',
   *   quantity: 0.1,
   *   price: 1800,
   *   type: 'LIMIT',
   *   side: 'SELL'
   * });
   */
  async placeOrder(order: Partial<Order>): Promise<Order> {
    if (!this.isConnected) {
      throw new Error('Not connected to NINX, please sign-in to connect to NINX')
    }

    try {
      const { symbol, quantity, price, type, side, triggerPrice } = order

      if (!symbol || !quantity || !side) {
        throw new Error('Symbol, quantity, and side are required')
      }

      // For Stop orders, triggerPrice is required
      if (type === 'STOP' && !triggerPrice) {
        throw new Error('Trigger price is required for Stop orders')
      }

      // Use our server-side API endpoint
      const response = await fetch('/api/connectors/ninx/order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          apiSecret: this.apiSecret,
          order: {
            symbol,
            quantity,
            price,
            type,
            side,
            triggerPrice
          }
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to place order')
      }

      return await response.json()
    } catch (error) {
      console.error('Failed to place order:', error)
      throw error
    }
  }

  /**
   * Get all orders from NINX
   * Retrieves all orders that have been placed
   * 
   * @returns {Promise<Order[]>} Promise resolving to an array of orders
   * @throws {Error} If not connected to NINX
   * 
   * @example
   * const orders = await ninxConnector.getOrders();
   * console.log(`You have ${orders.length} orders`);
   */
  async getOrders(): Promise<Order[]> {
    if (!this.isConnected) {
      throw new Error('Not connected to NINX, please sign-in to connect to NINX')
    }

    try {
      // Use our server-side API endpoint
      const response = await fetch('/api/connectors/ninx/orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          apiSecret: this.apiSecret
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch orders')
      }

      return await response.json()
    } catch (error) {
      console.error('Failed to fetch orders:', error)
      return []
    }
  }

  /**
    * Get account information from NINX
   * Retrieves account details including permissions, balances, and commission rates
   * 
   * @returns {Promise<any>} Promise resolving to account information
   * @throws {Error} If not connected to NINX
   * 
   * @example
   * const accountInfo = await ninxConnector.getAccountInfo();
   * console.log('Account balances:', accountInfo.balances);
   */
  async getAccountInfo(): Promise<any> {
    if (!this.isConnected) {
      throw new Error('Not connected to NINX, please sign-in to connect to NINX')
    }

    try {
      // Use our server-side API endpoint
      const response = await fetch('/api/connectors/ninx/account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          apiSecret: this.apiSecret
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch account info')
      }

      return await response.json()
    } catch (error) {
      console.error('Failed to fetch account info:', error)
      return null
    }
  }

  /**
   * Get account balance from NINX
   * Returns the available cash balance (in USDT) for trading
   * 
   * @returns {Promise<number>} Promise resolving to the account balance as a number
   * @throws {Error} If not connected to NINX
   * 
   * @example
   * const balance = await ninxConnector.getBalance();
   * console.log(`Available balance: $${balance.toFixed(2)} USDT`);
   */
  async getBalance(): Promise<number> {
    if (!this.isConnected) {
      throw new Error('Not connected to NINX, please sign-in to connect to NINX')
    }

    try {
      // Use our server-side API endpoint
      const response = await fetch('/api/connectors/ninx/balance', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          apiSecret: this.apiSecret
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch balance')
      }

      return await response.json()
    } catch (error) {
      console.error('Failed to fetch balance:', error)
      return 0
    }
  }

  /**
   * Get all positions from NINX
   * Retrieves all current positions with profit/loss values
   * 
   * @returns {Promise<Position[]>} Promise resolving to an array of positions
   * @throws {Error} If not connected to NINX
   * 
   * @example
   * const positions = await ninxConnector.getPositions();
   * 
   * // Calculate total P&L
   * const totalPnL = positions.reduce((sum, position) => sum + position.pnl, 0);
   * console.log(`Total P&L: $${totalPnL.toFixed(2)}`);
   */
  async getPositions(): Promise<Position[]> {
    if (!this.isConnected) {
      throw new Error('Not connected to NINX')
    }

    try {
      // Use our server-side API endpoint
        const response = await fetch('/api/connectors/ninx/positions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          apiSecret: this.apiSecret
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch positions')
      }

      return await response.json()
    } catch (error) {
      console.error('Failed to fetch positions:', error)
      return []
    }
  }

  /**
   * Get user's assets/holdings from NINX
   * Retrieves all cryptocurrencies owned by the user
   * 
   * @returns {Promise<any[]>} Promise resolving to an array of assets
   * @throws {Error} If not connected to NINX
   * 
   * @example
   * const assets = await ninxConnector.getUserAssets();
   * 
   * // Print all assets with non-zero balances
   * assets.filter(asset => parseFloat(asset.free) > 0)
   *   .forEach(asset => {
   *     console.log(`${asset.asset}: ${asset.free} (free) + ${asset.locked} (locked)`);
   *   });
   */
  async getUserAssets(): Promise<any[]> {
    try {
      return await this.makeAuthenticatedRequest('/api/connectors/ninx/assets', {
        apiKey: this.apiKey,
        apiSecret: this.apiSecret
      })
    } catch (error) {
      console.error('Failed to fetch user assets:', error)
      return []
    }
  }

  /**
   * Get order history from NINX
   * Retrieves historical orders with their execution details
   * 
   * @param {string} [symbol] Optional symbol to filter orders by (e.g., 'BTCUSDT')
   * @returns {Promise<any[]>} Promise resolving to an array of historical orders
   * @throws {Error} If not connected to NINX
   * 
   * @example
   * // Get all order history
   * const allOrders = await ninxConnector.getOrderHistory();
   * 
   * // Get order history for a specific symbol
   * const btcOrders = await ninxConnector.getOrderHistory('BTCUSDT');
   */
  async getOrderHistory(symbol?: string): Promise<any[]> {
    try {
      return await this.makeAuthenticatedRequest('/api/connectors/ninx/orders', {
        apiKey: this.apiKey,
        apiSecret: this.apiSecret,
        symbol: symbol || 'BTCUSDT' // Default to BTCUSDT if no symbol is provided
      })
    } catch (error) {
      console.error('Failed to fetch order history:', error)
      return []
    }
  }

  /**
   * Get trade history from NINX
   * Retrieves historical trades/executions
   * 
   * @param {string} [symbol='BTCUSDT'] Symbol to filter trades by
   * @returns {Promise<any[]>} Promise resolving to an array of historical trades
   * @throws {Error} If not connected to NINX
   * 
   * @example
   * // Get trades for the default symbol (BTCUSDT)
   * const btcTrades = await ninxConnector.getTradeHistory();
   * 
   * // Get trades for a specific symbol
   * const ethTrades = await ninxConnector.getTradeHistory('ETHUSDT');
   */
  async getTradeHistory(symbol: string = 'BTCUSDT'): Promise<any[]> {
    try {
      return await this.makeAuthenticatedRequest('/api/connectors/ninx/trades', {
        apiKey: this.apiKey,
        apiSecret: this.apiSecret,
        symbol: symbol
      })
    } catch (error) {
      console.error('Failed to fetch trade history:', error)
      return []
    }
  }

  /**
   * Get market data for all tickers on NINX
   * Retrieves current price, change percentage, and volume data
   * 
   * @returns {Promise<any[]>} Promise resolving to an array of ticker items
   * @throws {Error} If not connected to NINX
   * 
   * @example
   * const tickers = await ninxConnector.getTickers();
   * 
   * // Find Bitcoin price
   * const btcTicker = tickers.find(t => t.symbol === 'BTCUSDT');
   * console.log(`Bitcoin price: $${btcTicker.price}`);
   */
  async getTickers(): Promise<any[]> {
    if (!this.isConnected) {
      throw new Error('Not connected to NINX, please sign-in to connect to NINX')
    }

    try {
      // Use the server-side API endpoint to fetch ticker data
      const response = await fetch('/api/connectors/ninx/tickers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          apiSecret: this.apiSecret
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch ticker data')
      }

      const tickerData = await response.json()

      // Transform the data to include volume and format prices as numbers
      return tickerData.map((ticker: any) => ({
        symbol: ticker.symbol,
        price: parseFloat(ticker.price),
        priceChangePercent: parseFloat(ticker.priceChangePercent),
        volume: parseFloat(ticker.volume)
      }))
    } catch (error) {
      console.error('Error fetching NINX tickers:', error)
      throw error
    }
  }

  /**
   * Get LTP for a symbol from NINX
   * Retrieves the latest trading price for a given symbol
   * 
   * @param {string[]} symbols Array of symbols to fetch LTP for
   * @returns {Promise<any>} Promise resolving to an object containing LTP data
   * @throws {Error} If not connected to NINX
   */
  async getLTP(symbols: string[]): Promise<any> {
    if (!this.isConnected) {
      throw new Error('Not connected to NINX')
    }
    
    try {
      // Use the server-side API endpoint to fetch LTP
      const response = await fetch('/api/connectors/ninx/ltp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          apiSecret: this.apiSecret,
          symbols: symbols
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to fetch LTP data')
      }

      return await response.json()
    } catch (error) {
       console.error('Error fetching NINX LTP:', error)
      throw error
    }
  }

  /**
   * Get list of favorite symbols from NINX
   * Retrieves the user's saved favorite/starred symbols
   * 
   * @returns {Promise<string[]>} Promise resolving to an array of favorite symbol strings
   * @throws {Error} If not connected to NINX
   * 
   * @example
    * const favorites = await ninxConnector.getFavorites();
   * console.log('My favorite cryptocurrencies:', favorites);
   */
  async getFavorites(): Promise<string[]> {
    if (!this.isConnected) {
      throw new Error('Not connected to NINX, please sign-in to connect to NINX')
    }

    try {
      // Use the server-side API endpoint to fetch user favorites
      const response = await fetch('/api/connectors/ninx/favorites', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          apiSecret: this.apiSecret
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch favorites')
      }

      return await response.json()
    } catch (error) {
      console.error('Error fetching NINX favorites:', error)
      return [] // Return empty array on error
    }
  }

  /**
   * Get open orders from NINX
   * Retrieves all orders that are currently open/pending
   * 
   * @param {string} [symbol] Optional symbol to filter orders by (e.g., 'BTCUSDT')
   * @returns {Promise<any[]>} Promise resolving to an array of open orders
   * @throws {Error} If not connected to NINX 
   * 
   * @example
   * // Get all open orders
   * const allOpenOrders = await ninxConnector.getOpenOrders();
   * 
   * // Get open orders for a specific symbol
   * const btcOpenOrders = await ninxConnector.getOpenOrders('BTCUSDT');
   */
  async getOpenOrders(symbol?: string): Promise<any[]> {
    return this.makeAuthenticatedRequest('/api/connectors/ninx/openorders', {
      apiKey: this.apiKey,
      apiSecret: this.apiSecret,
      symbol: symbol
    })
  }

  /**
   * Get chart-formatted open orders from NINX
   * Retrieves open orders in a format suitable for chart display
   * 
   * @param {string} [symbol] Optional symbol to filter orders by (e.g., 'BTCUSDT')
   * @returns {Promise<any[]>} Promise resolving to an array of chart-formatted orders
   * @throws {Error} If not connected to NINX
   * 
   * @example
   * // Get all chart orders
   * const chartOrders = await ninxConnector.getChartOpenOrders();
   * 
   * // Get chart orders for a specific symbol
   * const btcChartOrders = await ninxConnector.getChartOpenOrders('BTCUSDT');
   */
  async getChartOpenOrders(symbol?: string): Promise<any[]> {
    try {
      if (!this.isConnectedToBroker()) {
        console.warn(
          'NINXConnector: Not connected, cannot fetch chart orders'
        )
        return []
      }

      console.log('NINXConnector: Fetching chart open orders')

      // Get open orders using the existing method
      const orders = await this.getOpenOrders(symbol)

      if (!orders || orders.length === 0) {
        console.log('NINXConnector: No open orders found')
        return []
      }

      // Add debug logging to see the structure of the orders
      if (orders.length > 0) {
        console.log(
          'NINXConnector: Raw NINX orders sample:',
          JSON.stringify(orders[0], null, 2)
        )
        console.log(`NINXConnector: Found ${orders.length} open orders`)
      }

      // Group orders by orderListId to handle OCO orders (orders with take profit and stop loss)
      const orderGroups: { [key: number]: any[] } = {}

      // Group orders by orderListId
      orders.forEach(order => {
        const orderListId = order.orderListId || 0
        if (orderListId > 0) {
          if (!orderGroups[orderListId]) {
            orderGroups[orderListId] = []
          }
          orderGroups[orderListId].push(order)
        }
      })

      // Transform the orders to match the format expected by the chart component
      const chartOrders: chartOrders[] = []

      // First process all orders that are not part of a group (orderListId is 0 or -1)
      orders.forEach(order => {
        const orderListId = order.orderListId || 0
        if (orderListId <= 0) {
          // Ensure we have valid values for all required fields
          const price = order.price || order.Price || '0'
          const origQty =
            order.origQty ||
            order.OrigQty ||
            order.quantity ||
            order.Quantity ||
            '0'
          const symbolValue = order.symbol || order.Symbol || ''
          const side = order.side || order.Side || 'BUY'

          chartOrders.push({
            time: new Date(order.time || order.Time || Date.now())
              .toISOString()
              .replace('T', ' ')
              .substring(0, 19),
            side: side,
            limitPrice: parseFloat(price),
            size: parseFloat(origQty),
            symbol: symbolValue.toLowerCase(),
            stopLoss: order.stopPrice ? parseFloat(order.stopPrice) : 0,
            takeProfit: 0, // Single orders don't have take profit
            orderId: order.orderId || order.OrderId || ''
          })
        }
      })

      // Then process grouped orders (OCO orders with take profit and stop loss)
      Object.entries(orderGroups).forEach(([orderListId, groupOrders]) => {
        if (groupOrders.length > 0) {
          // Find the main order (usually LIMIT), TP (usually LIMIT_MAKER), and SL (usually STOP_LOSS or STOP_LOSS_LIMIT)
          const mainOrder =
            groupOrders.find(o => o.type === 'LIMIT') || groupOrders[0]
          const takeProfit = groupOrders.find(o => o.type === 'LIMIT_MAKER')
          const stopLoss = groupOrders.find(
            o =>
              o.type === 'STOP_LOSS' ||
              o.type === 'STOP_LOSS_LIMIT' ||
              o.type === 'STOP_MARKET'
          )

          // Ensure we have valid values for all required fields
          const price = mainOrder.price || mainOrder.Price || '0'
          const origQty =
            mainOrder.origQty ||
            mainOrder.OrigQty ||
            mainOrder.quantity ||
            mainOrder.Quantity ||
            '0'
          const symbolValue = mainOrder.symbol || mainOrder.Symbol || ''
          const side = mainOrder.side || mainOrder.Side || 'BUY'

          // Calculate stop loss price - if price is 0, use stopPrice (market order), otherwise use price (limit order)
          let stopLossPrice = 0
          if (stopLoss) {
            stopLossPrice =
              parseFloat(stopLoss.price) === 0
                ? parseFloat(stopLoss.stopPrice || '0')
                : parseFloat(stopLoss.price || '0')
          }

          // Calculate take profit price
          let takeProfitPrice = 0
          if (takeProfit) {
            takeProfitPrice = parseFloat(takeProfit.price || '0')
          }

          chartOrders.push({
            time: new Date(mainOrder.time || mainOrder.Time || Date.now())
              .toISOString()
              .replace('T', ' ')
              .substring(0, 19),
            side: side,
            limitPrice: parseFloat(price),
            size: parseFloat(origQty),
            symbol: symbolValue.toLowerCase(),
            stopLoss: stopLossPrice,
            takeProfit: takeProfitPrice,
            orderId: mainOrder.orderId || mainOrder.OrderId || ''
          })
        }
      })

      console.log(
        'NINXConnector: Transformed chart orders:',
        chartOrders.length
      )
      return chartOrders
    } catch (error: any) {
      console.error(
        'NINXConnector: Error fetching chart open orders:',
        error
      )
      return []
    }
  }

  /**
   * Get options chain data for a specific underlying symbol and expiry
   * Retrieves options chain with strike prices, premiums, and other data
   * 
   * @param {string} underlyingSymbol The underlying symbol (e.g., 'BTC', 'ETH')
   * @param {string} [expiry] Optional expiry date (uses nearest expiry if omitted)
   * @returns {Promise<OptionsChainResponse>} Promise resolving to options chain data
   * @throws {Error} If not connected to NINX or options chain retrieval fails
   * 
   * @example
   * // Get options chain for BTC with default (nearest) expiry
   * const btcOptions = await ninxConnector.getOptionsChain('BTC');
   * 
   * // Get options chain for ETH with specific expiry
   * const ethOptions = await ninxConnector.getOptionsChain('ETH', '2023-06-30');
   */
  async getOptionsChain(underlyingSymbol: string, expiry?: string): Promise<OptionsChainResponse> {
    try {
      // If no expiry is provided, we need to fetch available expiry dates first
      if (!expiry) {
        console.log('No expiry provided, fetching available expiry dates first')
        // First, fetch available expiry dates by making a request to get exchange info
        try {
            const infoResponse = await fetch('/api/connectors/ninx/optionsinfo', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              symbol: underlyingSymbol
            })
          })
          console.log('infoResponse:', infoResponse)
          
          if (infoResponse.ok) {
            const infoData = await infoResponse.json()
            if (infoData.expiryDates && infoData.expiryDates.length > 0) {
              // Use the first available expiry date
              expiry = infoData.expiryDates[0]
              console.log('Using first available expiry date:', expiry)
            } else {
              throw new Error('No expiry dates available for this symbol')
            }
          } else {
            throw new Error('Failed to fetch expiry dates')
          }
        } catch (err) {
          console.error('Error fetching expiry dates:', err)
          throw new Error('Expiry date is required to fetch options chain')
        }
      }

      // Now we should have a valid expiry date
      if (!expiry) {
        throw new Error('Expiry date is required to fetch options chain')
      }

      // Build the query string with the symbol and expiry
      const queryParams = new URLSearchParams()
      queryParams.append('symbol', underlyingSymbol)
      queryParams.append('expiry', expiry)

      // Use our server-side API endpoint
      const response = await fetch(
        `/api/connectors/ninx/optionschain?${queryParams.toString()}`
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch options chain data')
      }

      const optionsData = await response.json()
      return optionsData
    } catch (error) {
      console.error('NINXConnector: Error in getOptionsChain:', error)
      throw error
    }
  }

  /**
   * Get the current access token
   * Provides the token needed for API authentication
   * 
   * @returns {Promise<string>} Promise resolving to the access token
   * 
   * @example
   * const token = await ninxConnector.getAccessToken();
   */
  async getAccessToken(): Promise<string> {
    // Nintrade might use a different authentication mechanism
    // For now, return the API key as the access token for compatibility
    return this.apiKey;
  }

  /**
   * Get the API key
   * Provides the API key used for authentication
   * 
   * @returns {Promise<string>} Promise resolving to the API key
   * 
   * @example
   * const apiKey = await ninxConnector.getApiKey();
   */
  async getApiKey(): Promise<string> {
    return this.apiKey;
  }

  /**
   * Get list of tradable instruments
   * Retrieves the list of instruments available for trading
   * Note: This is a mock implementation
   * 
   * @param {string} [exchange] Optional exchange to filter instruments by
   * @returns {Promise<any[]>} Promise resolving to an array of instruments
   * 
   * @example
   * const instruments = await ninxConnector.getInstruments();
   */
  async getInstruments(exchange?: string): Promise<any[]> {
    // mock data
    return ['mock instruments'];
  }

  /**
   * Cancel an open order on NINX
   * Cancels a specific order by its order ID
   * 
   * @param {string} orderId The order ID to cancel
   * @param {string} [symbol] Optional symbol parameter for the order
   * @returns {Promise<boolean>} Promise resolving to true if cancellation was successful
   * @throws {Error} If not connected to NINX or cancellation fails
   * 
   * @example
   * // Cancel an order by its ID
   * const success = await ninxConnector.cancelOrder('123456789');
   * if (success) {
   *   console.log('Order cancelled successfully');
   * }
   * 
   * @example
   * // Cancel an order with symbol for additional verification
   * const success = await ninxConnector.cancelOrder('123456789', 'BTCUSDT');
   */
  async cancelOrder(orderId: string, symbol?: string): Promise<boolean> {
    if (!this.isConnected) {
      throw new Error('Not connected to NINX, please sign-in to connect to NINX')
    }

    try {
      if (!orderId) {
        throw new Error('Order ID is required to cancel an order')
      }

      // Use our server-side API endpoint to cancel the order
        const response = await fetch('/api/connectors/ninx/cancelorder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          apiSecret: this.apiSecret,
          orderId: orderId,
          symbol: symbol
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to cancel order')
      }

      const result = await response.json()
      console.log('Order cancelled successfully:', result)
      return true
    } catch (error) {
      console.error('Failed to cancel order:', error)
      // Return false instead of throwing to allow graceful handling
      return false
    }
  }
}
